import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsApi, type Notification } from "./api";
import { useAuthStore } from "@/features/auth/store";

const KEY = ["notifications"] as const;

// Mirrors legacy TopBar behavior: poll every 60s while authenticated.
export function useNotifications() {
  const isAuthed = !!useAuthStore((s) => s.user);
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: notificationsApi.list,
    enabled: isAuthed,
    staleTime: 30_000,
    // Stop on an error (expired session, throttled); focus resumes it.
    refetchInterval: (q) => (q.state.status === "error" ? false : 60_000),
    refetchOnWindowFocus: true,
  });
}

export function useUnreadCount() {
  const q = useNotifications();
  const items = q.data ?? [];
  return items.filter((n) => !n.is_read).length;
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: [...KEY, "list"] });
      const prev = qc.getQueryData<Notification[]>([...KEY, "list"]);
      qc.setQueryData<Notification[]>([...KEY, "list"], (old) =>
        (old ?? []).map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData([...KEY, "list"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: [...KEY, "list"] });
      const prev = qc.getQueryData<Notification[]>([...KEY, "list"]);
      qc.setQueryData<Notification[]>([...KEY, "list"], (old) =>
        (old ?? []).map((n) => ({ ...n, is_read: true })),
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData([...KEY, "list"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: [...KEY, "list"] }),
  });
}
